import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import logo from '../../../img/logobranco.png';
import dashboardImage from '../../../videos/dashboard.png';
import analyticsVideo from '../../../videos/analytics.mp4';
import buscaPremiumVideo from '../../../videos/busca-premium.mp4';
import telegramBotVideo from '../../../videos/telegram-bot.mp4';
import mapaImpactoImage from '../../../img/mapa-impacto.png';

const BackgroundChart: React.FC = () => (
  <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: 'rgba(29, 78, 216, 0.1)' }} />
          <stop offset="100%" style={{ stopColor: 'rgba(29, 78, 216, 0)' }} />
        </linearGradient>
      </defs>
      <path
        d="M0,150 Q200,50 400,120 T800,150 T1200,100 T1600,180 L1600,0 L0,0 Z"
        fill="url(#chartGradient)"
        transform="translate(0, 200) scale(1.5)"
      />
      <path
        d="M-200,200 Q0,250 200,180 T600,220 T1000,160 T1400,250 T1800,200"
        stroke="#36ca9b"
        strokeWidth="3"
        fill="none"
        strokeDasharray="10 5"
        opacity="0.2"
        transform="translate(0, 50)"
      />
    </svg>
  </div>
);

const LandingPage: React.FC = () => {
  const auth = useAuth();

  const handleDemoClick = async () => {
    try {
      await auth.signinRedirect({
        extraQueryParams: {
          screen_hint: 'sign_up'
        }
      });
    } catch (error) {
      console.error('Erro ao redirecionar para cadastro:', error);
    }
  };

  const handleLoginClick = async () => {
    try {
      await auth.signinRedirect();
    } catch (error) {
      console.error('Erro ao redirecionar para login:', error);
    }
  };

  return (
    <div className="bg-accent text-dark font-sans relative">
      <BackgroundChart />
      <div className="relative z-10">

        {/* Header */}
        <header className="bg-primary sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-6 py-8">
            <div className="flex justify-between items-center">
              <img src={logo} alt="SinalInvest Logo" className="h-16" style={{ filter: 'brightness(0) invert(1)' }} />

              <nav className="hidden md:flex items-center gap-8 text-base font-medium text-gray-200">
                <a href="#solucao" className="hover:text-white transition-colors">Solução</a>
                <a href="#como-funciona" className="hover:text-white transition-colors">Como Funciona</a>
                <a href="#planos" className="hover:text-white transition-colors">Planos</a>
              </nav>

              <div className="hidden md:flex items-center gap-6">
                <button onClick={handleLoginClick} className="font-semibold text-gray-200 hover:text-white transition-all">
                  Entrar
                </button>
                <button onClick={handleDemoClick} className="px-6 py-2.5 rounded-lg font-bold text-dark bg-secondary hover:opacity-90 transition-all shadow-md">
                  Agendar Demo
                </button>
              </div>
              <div className="md:hidden flex items-center gap-3">
                <button onClick={handleLoginClick} className="text-sm font-semibold text-gray-200 hover:text-white transition-all">
                  Entrar
                </button>
                <button onClick={handleDemoClick} className="px-4 py-2 rounded-lg font-bold text-dark bg-secondary hover:opacity-90 transition-all shadow-md text-sm">
                  Demo
                </button>
              </div>
            </div>
          </div>
        </header>

        <main>

          {/* Hero Section */}
          <section className="min-h-screen flex items-center pt-16 pb-20">
            <div className="container mx-auto px-6 text-center">
              <div className="inline-block bg-primary/10 border border-primary/20 rounded-full px-5 py-2 mb-8">
                <span className="text-primary font-semibold text-sm uppercase tracking-wider">Para Escritórios de Investimentos</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-dark leading-tight mb-6">
                Inteligência de dados para <br /> sua <span className="text-primary">equipe de análise</span>.
              </h1>
              <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-600 mb-10">
                Equipamos escritórios e gestoras com infraestrutura de dados institucional. Seus analistas recebem sinais antes do mercado — via Dashboard, Telegram e relatórios estruturados.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16 px-4">
                <button onClick={handleDemoClick} className="px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg text-white bg-primary hover:scale-105 transition-transform shadow-lg">
                  Agendar uma Demo
                </button>
                <a href="#planos" className="px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg text-primary bg-white border-2 border-primary hover:bg-primary hover:text-white transition-all shadow-md">
                  Ver Planos
                </a>
              </div>

              <section className="py-4 md:py-6 mb-8 md:mb-10 border-y border-gray-200">
                <div className="max-w-4xl mx-auto px-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                    <div>
                      <div className="text-3xl font-extrabold text-primary mb-1">+50%</div>
                      <div className="text-sm text-gray-600">redução no tempo de triagem de notícias</div>
                    </div>
                    <div>
                      <div className="text-3xl font-extrabold text-primary mb-1">Tempo real</div>
                      <div className="text-sm text-gray-600">alertas antes do mercado reagir</div>
                    </div>
                    <div>
                      <div className="text-3xl font-extrabold text-primary mb-1">Multi-usuário</div>
                      <div className="text-sm text-gray-600">toda a equipe na mesma plataforma</div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="max-w-5xl mx-auto mt-12">
                <img
                  src={dashboardImage}
                  alt="Dashboard institucional SinalInvest"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>
          </section>

          {/* Features Intro */}
          <section id="solucao" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">
                  Uma infraestrutura completa de inteligência
                </h2>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                  Desenvolvida para escritórios que precisam de velocidade, profundidade e escala na análise de informações de mercado.
                </p>
              </div>
            </div>
          </section>

          {/* Feature Destaque: Mapas de Impacto */}
          <section className="py-24 bg-gradient-to-b from-white via-blue-50/40 to-white">
            <div className="container mx-auto px-6">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2 mb-5 shadow-lg">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="font-semibold text-sm uppercase tracking-wider">Feature Principal</span>
                  </div>
                  <h3 className="text-3xl md:text-5xl font-extrabold text-dark mb-5 leading-tight">
                    Mapas de Impacto <span className="text-primary">monitorados 24/7</span>
                  </h3>
                  <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                    Seu escritório cria mapas de impacto para cada empresa, organizados por tema.
                    A IA busca notícias sobre cada tema automaticamente, analisa o impacto e notifica sua equipe — sem parar.
                  </p>
                </div>

                <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
                  <img
                    src={mapaImpactoImage}
                    alt="Mapa de impacto de investimentos com temas e análise por IA"
                    className="w-full"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-dark mb-2 text-lg">Mapas por empresa</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Monte um mapa de impacto customizado para cada empresa da carteira, com temas, localidades e idiomas específicos.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-dark mb-2 text-lg">Busca 24/7 automatizada</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nossa engine varre fontes de notícias nos temas configurados sem pausa, incluindo fins de semana e feriados.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-dark mb-2 text-lg">Análise por IA</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Processamento massivo com inteligência artificial classifica sentimento, impacto e relevância de cada notícia.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Feature 1: Processamento de Dados */}
          <section className="py-24">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <video
                    src={analyticsVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <div className="inline-block bg-primary/10 rounded-full px-4 py-1.5 mb-4">
                    <span className="text-primary font-semibold text-sm">Processamento Massivo</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">
                    Seus analistas focam na decisão. Nossa IA faz a triagem.
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Processamos milhões de datapoints em tempo real para que sua equipe receba apenas o que importa: correlações ocultas, análise de sentimento institucional e impacto setorial mapeado em dashboards profissionais.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Feature 2: Alertas Institucionais */}
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <div className="inline-block bg-secondary/20 rounded-full px-4 py-1.5 mb-4">
                    <span className="text-green-700 font-semibold text-sm">Alertas Institucionais</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">
                    O sinal chega antes do ruído — para toda a equipe.
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Configure canais de alerta por analista ou por carteira. Quando a tese de uma empresa muda, o responsável é notificado no Telegram instantaneamente — sem spam, sem filtrar manualmente. Relatórios aprofundados chegam via e-mail no momento certo.
                  </p>
                </div>
                <div className="order-1 md:order-2 flex justify-center items-center">
                  <video
                    src={telegramBotVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full max-w-[280px] sm:max-w-[320px] md:max-w-sm h-auto"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Feature 3: Busca Contextual */}
          <section className="py-24">
            <div className="container mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="order-2 md:order-1">
                  <video
                    src={buscaPremiumVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="rounded-xl shadow-2xl w-full"
                  />
                </div>
                <div className="order-1 md:order-2">
                  <div className="inline-block bg-primary/10 rounded-full px-4 py-1.5 mb-4">
                    <span className="text-primary font-semibold text-sm">Pesquisa Inteligente</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-dark mb-4 sm:mb-6">
                    Pergunte ao mercado. Obtenha respostas estruturadas.
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    Seus analistas pesquisam por contexto, não por palavra-chave. Pergunte "Como a alta do petróleo afeta a margem da Empresa X?" e nossa IA varre terabytes de dados entregando a resposta documentada e pronta para o relatório do cliente.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Como Funciona */}
          <section id="como-funciona" className="py-24 bg-gradient-to-b from-white to-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">Como seu escritório começa</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">Onboarding institucional em três etapas. Sem complexidade técnica.</p>
              </div>

              <div className="max-w-5xl mx-auto">

                {/* Step 1 */}
                <div className="mb-16">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold shadow-lg">
                        1
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Configure o escritório</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Crie a conta institucional do seu escritório em menos de 5 minutos. Configure carteiras, setores monitorados e as empresas que sua equipe acompanha.
                      </p>
                      <div className="mt-6">
                        <button onClick={handleDemoClick} className="px-6 py-3 rounded-lg font-semibold text-white bg-primary hover:bg-blue-800 transition-all shadow-md">
                          Agendar Demo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center mb-16">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                {/* Step 2 */}
                <div className="mb-16">
                  <div className="flex flex-col md:flex-row-reverse items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-secondary text-dark flex items-center justify-center text-3xl font-bold shadow-lg">
                        2
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Adicione sua equipe</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Convide seus analistas e gestores. Cada usuário recebe alertas configurados para suas responsabilidades. Controle de acesso por carteira ou setor.
                      </p>
                      <div className="mt-6">
                        <a href="#planos" className="inline-block px-6 py-3 rounded-lg font-semibold text-primary bg-white border-2 border-primary hover:bg-primary hover:text-white transition-all shadow-md">
                          Ver Planos
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center mb-16">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                {/* Step 3 */}
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-bold shadow-lg">
                        3
                      </div>
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-2xl font-bold text-dark mb-4">Sua equipe opera em outro nível</h3>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">
                        Inteligência entregue nos canais que sua equipe já usa:
                      </p>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Telegram</h4>
                          </div>
                          <p className="text-sm text-gray-600">Alertas críticos entregues diretamente ao analista responsável pela posição.</p>
                        </div>

                        <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">E-mail</h4>
                          </div>
                          <p className="text-sm text-gray-600">Relatórios aprofundados prontos para apresentação ao comitê ou ao cliente.</p>
                        </div>

                        <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Dashboard</h4>
                          </div>
                          <p className="text-sm text-gray-600">Painel centralizado com visão de todas as carteiras monitoradas pela equipe.</p>
                        </div>

                        <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-orange-600 flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <h4 className="font-bold text-dark">Relatórios</h4>
                          </div>
                          <p className="text-sm text-gray-600">Análises documentadas com contexto, impacto e recomendação estratégica por setor.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-16">
                <div className="bg-primary rounded-2xl p-8 text-white max-w-3xl mx-auto shadow-xl">
                  <h3 className="text-3xl font-bold mb-4">Pronto para ver na prática?</h3>
                  <p className="text-lg opacity-90 mb-6">Agende uma demonstração e veja como escritórios de investimentos operam com a SinalInvest.</p>
                  <button onClick={handleDemoClick} className="px-8 py-4 rounded-lg font-bold text-primary bg-white hover:scale-105 transition-transform shadow-lg">
                    Agendar Demo Gratuita
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Comparativo Section */}
          <section className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-extrabold text-dark mb-4">A diferença é operacional</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">Veja como escritórios com SinalInvest ganham vantagem competitiva real</p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-700 mb-6">O Escritório Tradicional</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Analistas perdem horas triando notícias irrelevantes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Alertas chegam atrasados — o mercado já reagiu</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Sem visão consolidada de todas as carteiras em tempo real</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-500 text-xl">✗</span>
                      <span className="text-gray-600">Relatórios para clientes montados manualmente</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-primary/5 rounded-2xl p-8 border-2 border-primary">
                  <h3 className="text-2xl font-bold text-primary mb-6">O Escritório SinalInvest</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">IA filtra e entrega apenas o que muda a tese de investimento</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Alertas em tempo real antes do mercado reagir</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Dashboard unificado com todas as carteiras e analistas</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <span className="text-gray-700">Relatórios estruturados gerados automaticamente</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Demo CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary via-blue-700 to-primary">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
                  <div className="inline-block bg-white/20 rounded-full px-6 py-2 mb-6">
                    <span className="text-white font-semibold text-sm md:text-base uppercase tracking-wider">Sem compromisso</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
                    Demo exclusiva para seu escritório
                  </h2>
                  <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto">
                    Apresentamos a plataforma para sua equipe, configuramos um ambiente com as suas empresas monitoradas e mostramos o ROI operacional na prática.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                    <div className="flex items-center gap-3 text-white">
                      <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base md:text-lg">Demo personalizada para seu escritório</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <svg className="w-6 h-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-base md:text-lg">Onboarding assistido pela equipe SinalInvest</span>
                    </div>
                  </div>
                  <button
                    onClick={handleDemoClick}
                    className="px-10 py-4 rounded-lg font-bold text-lg text-primary bg-white hover:scale-105 transition-transform shadow-xl hover:shadow-2xl"
                  >
                    Agendar Demo Agora
                  </button>
                  <p className="text-white/80 text-sm mt-6">
                    Resposta em até 24 horas úteis.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="planos" className="py-24 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-extrabold text-dark mb-4">Infraestrutura dedicada para o seu escritório</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Cada cliente recebe um ambiente próprio, dimensionado para o volume da equipe e da carteira. Contrato sob consulta, com escopo definido junto ao seu escritório.
                </p>
              </div>

              <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-lg p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-bold text-dark mb-4">O que está incluso</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3 text-gray-700">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span>Ambiente dedicado para o seu escritório</span>
                      </li>
                      <li className="flex items-start gap-3 text-gray-700">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span>Usuários e empresas monitoradas dimensionados conforme necessidade</span>
                      </li>
                      <li className="flex items-start gap-3 text-gray-700">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span>Alertas via Telegram e e-mail por analista</span>
                      </li>
                      <li className="flex items-start gap-3 text-gray-700">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span>Dashboard com análise de IA e mapas de impacto</span>
                      </li>
                      <li className="flex items-start gap-3 text-gray-700">
                        <span className="text-lg text-green-500 flex-shrink-0">✔</span>
                        <span>Onboarding assistido e suporte direto com a equipe SinalInvest</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-200 pt-8 md:pt-0 md:pl-8">
                    <p className="text-sm uppercase tracking-widest text-gray-400 font-semibold mb-2">Investimento</p>
                    <div className="text-3xl font-extrabold text-blue-700 mb-4">Sob consulta</div>
                    <p className="text-sm text-gray-600 mb-6">
                      O valor é definido em conjunto com seu escritório, considerando o tamanho da equipe, a quantidade de empresas monitoradas e o nível de customização desejado.
                    </p>
                    <button
                      type="button"
                      onClick={handleDemoClick}
                      className="w-full p-3 text-base font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 transition-all duration-200"
                    >
                      Falar com Especialista
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="bg-primary text-white">
            <div className="container mx-auto px-6 py-20 text-center">
              <h2 className="text-4xl font-extrabold mb-4">Seu escritório merece uma infraestrutura institucional.</h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">Fale com nossa equipe e veja como a SinalInvest encaixa na operação do seu escritório.</p>
              <button onClick={handleDemoClick} className="px-10 py-4 rounded-lg font-bold text-primary bg-white hover:scale-105 transition-transform shadow-lg">
                Agendar minha Demo
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-dark text-white">
          <div className="container mx-auto px-6 py-12">
            <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
              <div className="mb-8 md:mb-0">
                <img src={logo} alt="SinalInvest Logo" className="h-10 mb-4 mx-auto md:mx-0" style={{ filter: 'brightness(0) invert(1)' }} />
                <p className="text-gray-400">Inteligência de dados para escritórios de investimentos.</p>
              </div>
              <div className="flex gap-6 font-medium text-gray-300">
                <a href="#solucao" className="hover:text-secondary transition-colors">Solução</a>
                <a href="#planos" className="hover:text-secondary transition-colors">Planos</a>
                <Link to="/termos-de-uso" className="hover:text-secondary transition-colors">Termos</Link>
                <Link to="/politica-de-privacidade" className="hover:text-secondary transition-colors">Privacidade</Link>
              </div>
            </div>
            <div className="text-center text-gray-500 mt-10 pt-8 border-t border-gray-700">
              <p>&copy; {new Date().getFullYear()} SinalInvest. Todos os direitos reservados.</p>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default LandingPage;
